import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { createTabSyncChannel } from "../realtime/tabSyncChannel";
import { createTabOwnershipCoordinator } from "../realtime/tabOwnership";
import {
  createRealtimeMirrorStore,
  DEFAULT_REALTIME_SNAPSHOT,
} from "../realtime/realtimeMirrorStore";

export const MESSAGE_TYPES = {
  INIT_HANDSHAKE: "init-handshake",
  CLIENT_HANDSHAKE: "client-handshake",
  HANDSHAKE_COMPLETED: "handshake-completed",
  GET_FRIENDS: "get-friends",
  FRIEND_REQUEST_ACCEPTED: "friend-request-accepted",
  FRIEND_STATUS_UPDATE: "friend-status-update",
  LOBBY_MEMBER_STATUS_UPDATE: "lobby-member-status-update",
  MATCH_FOUND_NOTIFICATION: "match-found-notification",
  MATCH_ACCEPTANCE_UPDATE: "match-acceptance-update",
  MATCH_ACCEPTANCE_CANCELLED: "match-acceptance-cancelled",
  MAP_VOTE_STARTED: "map-vote-started",
  MAP_VOTE_UPDATE: "map-vote-update",
  MAP_VOTE_RESOLVED: "map-vote-resolved",
  INVITE_RECEIVED: "invite-received",
  INVITE_EXPIRED: "invite-expired",
  INVITE_DECLINED: "invite-declined",
  INVITE_CANCELLED: "invite-cancelled",
  LOBBY_CREATED: "lobby-created",
  INVITE_ACCEPTED: "invite-accepted",
  INVITE_ERROR: "invite-error",
  INVITE_REJECTED_FULL: "invite-rejected-full",
  LOBBY_PLAYER_LEFT: "lobby-player-left",
  LOBBY_PLAYER_REMOVED_SILENT: "lobby-player-removed-silent",
  LOBBY_ADDED_PLAYER: "lobby-added-player",
  LOBBY_OPTIONS_UPDATED: "lobby-options-updated",
  LOBBY_MAPS_UPDATED: "lobby-maps-updated",
  LOBBY_QUEUE_MODE_UPDATED: "lobby-queue-mode-updated",
  LOBBY_SERVER_LOCATIONS_UPDATED: "lobby-server-locations-updated",
  LOBBY_PLAYER_KICKED: "lobby-player-kicked",
  FRIEND_REQUEST_RECEIVED: "friend-request-received",
  FRIEND_REQUEST_ACCEPTED_NOTIFICATION: "friend-request-accepted-notification",
  FRIEND_REQUEST_DECLINED_NOTIFICATION: "friend-request-declined-notification",
  FRIEND_DELETED_NOTIFICATION: "friend-deleted-notification",
  FRIEND_REQUEST_ERROR: "friend-request-error",
  MATCHMAKING_STARTED_NOTIFICATION: "matchmaking-started-notification",
  MATCHMAKING_STOPPED_NOTIFICATION: "matchmaking-stopped-notification",
  LOBBY_CODE_NOT_FOUND: "lobby-code-not-found",
  LOBBY_FULL: "lobby-full",
  JOINED_LOBBY_BY_CODE: "joined-lobby-by-code",
  SOLO_LOBBY_ERROR: "solo-lobby-error",
  USER_LOGOUT: "user-logout",
  SESSION_ENDED: "session-ended",
  SET_DISPLAY_STATUS: "set-display-status",
  DISPLAY_STATUS_UPDATED: "display-status-updated",
  DISPLAY_STATUS_ERROR: "display-status-error",
  MATCH_SERVER_READY: "match-server-ready",
  QUEUE_STATUS_UPDATE: "queue-status-update",
  PLATFORM_STATS_UPDATE: "platform-stats-update",
  FRIENDS_BOOTSTRAP: "friends-bootstrap",
  FRIEND_REQUESTS_BOOTSTRAP: "friend-requests-bootstrap",
  FRIENDS_LIST: "friends-list",
  LOBBY_SETTINGS_DENIED: "lobby-settings-denied",
};

export const CONNECTION_STATES = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

const MIRROR_CHANNEL_PREFIX = "ouro-realtime-mirror";
const CHANNEL_MESSAGE_TYPES = {
  SNAPSHOT: "mirror-snapshot",
  MESSAGE: "mirror-message",
  SNAPSHOT_REQUEST: "mirror-snapshot-request",
  SNAPSHOT_RESPONSE: "mirror-snapshot-response",
};

export const OWNER_ONLY_MESSAGE_IDS = new Set([
  "invite-send",
  "invite-decline",
  "invite-cancel",
  "invite-accept",
  "lobby-map-update",
  "lobby-maps-toggle-all",
  "lobby-server-location-update",
  "lobby-server-locations-toggle-all",
  "lobby-options-update",
  "lobby-queue-mode-update",
  "accept-match",
  "cast-map-vote",
  "leave-lobby",
  "kick-player-from-lobby",
  "lobby-matchmaking-started",
  "matchmaking-stopped",
  "join-lobby-by-code",
  "create-solo-lobby",
  MESSAGE_TYPES.USER_LOGOUT,
  MESSAGE_TYPES.SET_DISPLAY_STATUS,
]);

const DEFAULT_PUBLIC_STATE = {
  connectionState: CONNECTION_STATES.DISCONNECTED,
  clientId: null,
  isConnected: false,
  isOwner: false,
  ownershipState: "passive",
};

function getSocketConnectionConfig() {
  if (process.env.NODE_ENV === "production") {
    return {
      socketUrl: "/game",
      socketOptions: {
        path: "/edge/socket.io/",
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        timeout: 20000,
        autoConnect: true,
        withCredentials: true,
        secure: true,
      },
    };
  }

  return {
    socketUrl: "http://localhost:3010/game",
    socketOptions: {
      path: "/socket.io/",
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      autoConnect: true,
      withCredentials: true,
      secure: false,
    },
  };
}

function normalizeSocketMessage(messageId, message) {
  if (message && typeof message === "object") {
    return { ...message, messageId };
  }

  return { messageId, data: message };
}

function resolveUserRuntimeKey(user) {
  const value =
    user?.userId || user?.steamId || user?.steamId64 || user?.steam64Id || null;

  if (value == null || value === "") {
    return null;
  }

  return String(value);
}

export function createWebSocketManager({
  ioFactory = io,
  ownership,
  mirrorStore,
  channel,
} = {}) {
  if (!ownership) {
    throw new Error("createWebSocketManager: ownership is required");
  }
  if (!mirrorStore) {
    throw new Error("createWebSocketManager: mirrorStore is required");
  }
  if (!channel) {
    throw new Error("createWebSocketManager: channel is required");
  }

  let socket = null;
  let user = null;
  let unsubscribeOwnership = null;
  let unsubscribeChannel = null;
  let pendingOwnerOnlySend = null;
  let latestMirrorEpoch = 0;
  let latestMirrorOwnerTabId = null;
  const listeners = new Set();

  const getOwnershipSnapshot = () => {
    const snapshot = ownership.getState?.();
    return snapshot && typeof snapshot === "object" ? snapshot : {};
  };

  const isOwner = () => ownership.isOwner?.() === true;

  const getMirrorSnapshot = () => {
    if (typeof mirrorStore.getSnapshot === "function") {
      return mirrorStore.getSnapshot();
    }
    return { ...DEFAULT_REALTIME_SNAPSHOT };
  };

  const getPublicState = () => {
    const ownershipSnapshot = getOwnershipSnapshot();
    const snapshot = getMirrorSnapshot();
    const connectionState =
      snapshot.connectionState || CONNECTION_STATES.DISCONNECTED;
    const clientId =
      snapshot.clientId !== undefined ? snapshot.clientId : socket?.id || null;
    const connected =
      socket?.connected === true ||
      connectionState === CONNECTION_STATES.CONNECTED;

    return {
      connectionState,
      clientId,
      isConnected: connected,
      isOwner: isOwner(),
      ownershipState: ownershipSnapshot.role || snapshot.ownershipState || "passive",
    };
  };

  const notifyListeners = (message = null) => {
    const nextState = getPublicState();
    listeners.forEach((listener) => {
      try {
        listener({
          ...nextState,
          message,
        });
      } catch (error) {
        console.error("[Socket.IO] Error in listener:", error);
      }
    });
  };

  const syncMirrorVersionFromOwnership = () => {
    const ownershipSnapshot = getOwnershipSnapshot();
    const ownershipEpoch =
      typeof ownershipSnapshot.epoch === "number" ? ownershipSnapshot.epoch : 0;

    if (ownershipEpoch > latestMirrorEpoch) {
      latestMirrorEpoch = ownershipEpoch;
    }

    if (typeof ownershipSnapshot.ownerTabId === "string") {
      latestMirrorOwnerTabId = ownershipSnapshot.ownerTabId;
    } else if (
      Object.prototype.hasOwnProperty.call(ownershipSnapshot, "ownerTabId")
    ) {
      latestMirrorOwnerTabId = null;
    }
  };

  const getCurrentMirrorVersion = () => {
    const ownershipSnapshot = getOwnershipSnapshot();
    const ownershipEpoch =
      typeof ownershipSnapshot.epoch === "number" ? ownershipSnapshot.epoch : 0;

    return {
      epoch: Math.max(latestMirrorEpoch, ownershipEpoch),
      ownerTabId: Object.prototype.hasOwnProperty.call(
        ownershipSnapshot,
        "ownerTabId"
      )
        ? ownershipSnapshot.ownerTabId
        : latestMirrorOwnerTabId,
    };
  };

  const rememberAcceptedMirrorPayload = (payload) => {
    if (typeof payload?.sourceEpoch === "number") {
      latestMirrorEpoch = Math.max(latestMirrorEpoch, payload.sourceEpoch);
    }
    if (Object.prototype.hasOwnProperty.call(payload || {}, "sourceOwnerTabId")) {
      latestMirrorOwnerTabId =
        typeof payload?.sourceOwnerTabId === "string"
          ? payload.sourceOwnerTabId
          : null;
    }
  };

  const shouldAcceptMirroredPayload = (payload) => {
    if (!payload || typeof payload !== "object") {
      return false;
    }

    if (typeof payload.sourceEpoch !== "number") {
      return false;
    }

    const { epoch: currentEpoch, ownerTabId: currentOwnerTabId } =
      getCurrentMirrorVersion();

    if (payload.sourceEpoch < currentEpoch) {
      return false;
    }

    if (
      payload.sourceEpoch === currentEpoch &&
      currentOwnerTabId == null &&
      payload.sourceOwnerTabId != null
    ) {
      return false;
    }

    if (
      payload.sourceEpoch === currentEpoch &&
      currentOwnerTabId &&
      payload.sourceOwnerTabId &&
      payload.sourceOwnerTabId !== currentOwnerTabId
    ) {
      return false;
    }

    return true;
  };

  const emitSocketMessage = (message, activeSocket = socket) => {
    if (!activeSocket || !message?.messageId) {
      return false;
    }

    activeSocket.emit(message.messageId, message);
    return true;
  };

  const flushPendingOwnerOnlySend = (activeSocket = socket) => {
    if (!activeSocket?.connected || !pendingOwnerOnlySend) {
      return false;
    }

    const queuedMessage = pendingOwnerOnlySend;
    pendingOwnerOnlySend = null;
    return emitSocketMessage(queuedMessage, activeSocket);
  };

  const broadcastMirrorState = (snapshot, message = null) => {
    const ownershipSnapshot = getOwnershipSnapshot();
    const sourceOwnerTabId = ownershipSnapshot.ownerTabId || null;
    const sourceEpoch =
      typeof ownershipSnapshot.epoch === "number" ? ownershipSnapshot.epoch : 0;

    try {
      channel.post({
        type: message
          ? CHANNEL_MESSAGE_TYPES.MESSAGE
          : CHANNEL_MESSAGE_TYPES.SNAPSHOT,
        sourceOwnerTabId,
        sourceEpoch,
        snapshot,
        ...(message ? { message } : {}),
      });
    } catch (error) {
      /* ignore channel failures */
    }
  };

  const publishSnapshot = ({ snapshot, message = null, broadcast = false }) => {
    if (broadcast) {
      broadcastMirrorState(snapshot, message);
    }
    notifyListeners(message);
  };

  const updateMirrorConnectionState = (
    overrides = {},
    { broadcast = isOwner(), message = null } = {}
  ) => {
    const ownershipSnapshot = getOwnershipSnapshot();
    const snapshot = mirrorStore.applyEvent({
      type: "connection-state",
      connectionState:
        Object.prototype.hasOwnProperty.call(overrides, "connectionState")
          ? overrides.connectionState
          : isOwner()
            ? CONNECTION_STATES.CONNECTING
            : CONNECTION_STATES.DISCONNECTED,
      clientId: Object.prototype.hasOwnProperty.call(overrides, "clientId")
        ? overrides.clientId
        : socket?.id || null,
      isOwner: Object.prototype.hasOwnProperty.call(overrides, "isOwner")
        ? overrides.isOwner
        : isOwner(),
      ownershipState: Object.prototype.hasOwnProperty.call(
        overrides,
        "ownershipState"
      )
        ? overrides.ownershipState
        : ownershipSnapshot.role || "passive",
    });

    publishSnapshot({ snapshot, message, broadcast });
    return snapshot;
  };

  const applyInboundMessage = (message, { broadcast = isOwner() } = {}) => {
    const snapshot = mirrorStore.applyEvent(message);
    publishSnapshot({ snapshot, message, broadcast });
    return snapshot;
  };

  const openSocket = (factory = ioFactory) => {
    if (socket || !user) {
      return socket;
    }

    const { socketUrl, socketOptions } = getSocketConnectionConfig();

    updateMirrorConnectionState({
      connectionState: CONNECTION_STATES.CONNECTING,
      clientId: null,
    });

    try {
      socket = factory(socketUrl, socketOptions);
      setupEventListeners(socket);

      if (socket?.connected) {
        updateMirrorConnectionState({
          connectionState: CONNECTION_STATES.CONNECTED,
          clientId: socket.id || null,
        });
        flushPendingOwnerOnlySend(socket);
      }

      return socket;
    } catch (error) {
      console.error("[Socket.IO] Failed to create connection:", error);
      updateMirrorConnectionState({
        connectionState: CONNECTION_STATES.ERROR,
        clientId: null,
      });
      return null;
    }
  };

  function setupEventListeners(activeSocket = socket) {
    if (!activeSocket) {
      return;
    }

    activeSocket.on("connect", () => {
      if (socket !== activeSocket) return;
      updateMirrorConnectionState({
        connectionState: CONNECTION_STATES.CONNECTED,
        clientId: activeSocket.id || null,
      });
      flushPendingOwnerOnlySend(activeSocket);
    });

    activeSocket.on("disconnect", () => {
      if (socket !== activeSocket) return;
      updateMirrorConnectionState({
        connectionState: CONNECTION_STATES.DISCONNECTED,
        clientId: null,
      });
    });

    activeSocket.on("connect_error", () => {
      if (socket !== activeSocket) return;
      updateMirrorConnectionState({
        connectionState: CONNECTION_STATES.ERROR,
        clientId: null,
      });
    });

    activeSocket.io?.on("reconnect_attempt", () => {
      if (socket !== activeSocket) return;
      updateMirrorConnectionState({
        connectionState: CONNECTION_STATES.RECONNECTING,
        clientId: activeSocket.id || null,
      });
    });

    activeSocket.io?.on("reconnect", () => {
      if (socket !== activeSocket) return;
      updateMirrorConnectionState({
        connectionState: CONNECTION_STATES.CONNECTED,
        clientId: activeSocket.id || null,
      });
    });

    activeSocket.on(MESSAGE_TYPES.INIT_HANDSHAKE, (message) => {
      if (socket !== activeSocket) return;

      activeSocket.emit(MESSAGE_TYPES.CLIENT_HANDSHAKE, {
        messageId: MESSAGE_TYPES.CLIENT_HANDSHAKE,
        clientId: message?.clientId,
      });

      applyInboundMessage(
        normalizeSocketMessage(MESSAGE_TYPES.INIT_HANDSHAKE, message)
      );
    });

    activeSocket.on(MESSAGE_TYPES.HANDSHAKE_COMPLETED, (message) => {
      if (socket !== activeSocket) return;

      const steamId = user?.steamId || user?.steamId64 || user?.steam64Id || null;
      if (steamId) {
        activeSocket.emit(MESSAGE_TYPES.GET_FRIENDS, {
          messageId: MESSAGE_TYPES.GET_FRIENDS,
          steamId,
        });
      }

      applyInboundMessage(
        normalizeSocketMessage(MESSAGE_TYPES.HANDSHAKE_COMPLETED, message)
      );
    });

    activeSocket.on(MESSAGE_TYPES.MATCH_FOUND_NOTIFICATION, (message, ack) => {
      if (socket !== activeSocket) return;

      try {
        if (typeof ack === "function") {
          ack({
            ok: true,
            receivedAt: Date.now(),
            socketId: activeSocket.id || null,
          });
        }
      } catch (error) {
        /* never let ack failures break handlers */
      }

      applyInboundMessage(
        normalizeSocketMessage(MESSAGE_TYPES.MATCH_FOUND_NOTIFICATION, message)
      );
    });

    Object.values(MESSAGE_TYPES).forEach((messageType) => {
      if (
        messageType === MESSAGE_TYPES.INIT_HANDSHAKE ||
        messageType === MESSAGE_TYPES.HANDSHAKE_COMPLETED ||
        messageType === MESSAGE_TYPES.MATCH_FOUND_NOTIFICATION
      ) {
        return;
      }

      activeSocket.on(messageType, (message) => {
        if (socket !== activeSocket) return;
        applyInboundMessage(normalizeSocketMessage(messageType, message));
      });
    });
  }

  const handleOwnershipChange = () => {
    const ownershipSnapshot = getOwnershipSnapshot();
    syncMirrorVersionFromOwnership();

    if (!isOwner()) {
      pendingOwnerOnlySend = null;
      if (socket) {
        try {
          socket.disconnect();
        } catch (error) {
          /* ignore disconnect failures */
        }
        socket = null;
      }

      updateMirrorConnectionState(
        {
          connectionState: CONNECTION_STATES.DISCONNECTED,
          clientId: null,
          isOwner: false,
          ownershipState: ownershipSnapshot.role || "passive",
        },
        { broadcast: false }
      );
      if (user) {
        try {
          channel.post({
            type: CHANNEL_MESSAGE_TYPES.SNAPSHOT_REQUEST,
            sourceEpoch:
              typeof ownershipSnapshot.epoch === "number"
                ? ownershipSnapshot.epoch
                : 0,
          });
        } catch (error) {
          /* ignore channel failures */
        }
      }
      return;
    }

    if (user && !socket) {
      openSocket(ioFactory);
      return;
    }

    updateMirrorConnectionState(
      {
        connectionState: socket?.connected
          ? CONNECTION_STATES.CONNECTED
          : CONNECTION_STATES.CONNECTING,
        clientId: socket?.id || null,
        isOwner: true,
        ownershipState: ownershipSnapshot.role || "owner",
      },
      { broadcast: true }
    );
  };

  const handleChannelMessage = (payload) => {
    if (!payload || typeof payload !== "object") {
      return;
    }

    const ownershipSnapshot = getOwnershipSnapshot();
    if (payload.type === CHANNEL_MESSAGE_TYPES.SNAPSHOT_REQUEST) {
      if (!isOwner()) {
        return;
      }

      const snapshot = getMirrorSnapshot();
      try {
        channel.post({
          type: CHANNEL_MESSAGE_TYPES.SNAPSHOT_RESPONSE,
          sourceOwnerTabId: ownershipSnapshot.ownerTabId || null,
          sourceEpoch:
            typeof ownershipSnapshot.epoch === "number"
              ? ownershipSnapshot.epoch
              : 0,
          snapshot,
        });
      } catch (error) {
        /* ignore channel failures */
      }
      return;
    }

    if (
      isOwner() &&
      payload.sourceOwnerTabId &&
      payload.sourceOwnerTabId === ownershipSnapshot.ownerTabId
    ) {
      return;
    }

    if (
      payload.type === CHANNEL_MESSAGE_TYPES.SNAPSHOT ||
      payload.type === CHANNEL_MESSAGE_TYPES.SNAPSHOT_RESPONSE
    ) {
      if (!payload.snapshot || !shouldAcceptMirroredPayload(payload)) {
        return;
      }
      rememberAcceptedMirrorPayload(payload);
      mirrorStore.replaceSnapshot(payload.snapshot);
      notifyListeners();
      return;
    }

    if (payload.type === CHANNEL_MESSAGE_TYPES.MESSAGE && payload.snapshot) {
      if (!shouldAcceptMirroredPayload(payload)) {
        return;
      }
      rememberAcceptedMirrorPayload(payload);
      mirrorStore.replaceSnapshot(payload.snapshot);
      notifyListeners(payload.message || null);
    }
  };

  const ensureSubscriptions = () => {
    if (!unsubscribeOwnership && typeof ownership.subscribe === "function") {
      unsubscribeOwnership = ownership.subscribe(handleOwnershipChange);
    }
    if (!unsubscribeChannel && typeof channel.subscribe === "function") {
      unsubscribeChannel = channel.subscribe(handleChannelMessage);
    }
  };

  const connect = (nextUser) => {
    user = nextUser;
    ensureSubscriptions();
    ownership.start?.();

    if (!isOwner()) {
      return false;
    }

    openSocket(ioFactory);
    return true;
  };

  const waitForCondition = (predicate, timeoutMs = 250) =>
    new Promise((resolve) => {
      if (predicate()) {
        resolve(true);
        return;
      }

      let settled = false;
      let timeoutId = null;

      const listener = () => {
        if (predicate()) {
          finish(true);
        }
      };

      const finish = (result) => {
        if (settled) {
          return;
        }
        settled = true;
        listeners.delete(listener);
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        resolve(result);
      };

      listeners.add(listener);
      timeoutId = setTimeout(() => {
        finish(Boolean(predicate()));
      }, timeoutMs);
    });

  const send = (message, { requireOwner } = {}) => {
    const messageId = message?.messageId;
    if (!messageId) {
      return false;
    }
    const mustOwn =
      requireOwner !== undefined
        ? Boolean(requireOwner)
        : OWNER_ONLY_MESSAGE_IDS.has(messageId);

    if (mustOwn && !isOwner()) {
      ownership.requestOwnership?.({
        reason: "send",
        messageId,
      });
    }

    if (mustOwn && !isOwner()) {
      return false;
    }

    if (!isOwner()) {
      return false;
    }

    if (!socket) {
      openSocket(ioFactory);
    }

    if (!socket?.connected) {
      if (mustOwn && socket) {
        pendingOwnerOnlySend = message;
        return false;
      }
      return false;
    }

    return emitSocketMessage(message, socket);
  };

  const sendOwnerAction = (message) => send(message, { requireOwner: true });

  const performLogout = async ({
    httpLogout = async () => {},
    clearLocalSession = async () => {},
  } = {}) => {
    if (!isOwner()) {
      ownership.requestOwnership?.({
        reason: "logout",
        messageId: MESSAGE_TYPES.USER_LOGOUT,
      });

      if (!isOwner()) {
        await waitForCondition(() => isOwner(), 250);
      }
    }

    if (isOwner()) {
      if (!socket) {
        openSocket(ioFactory);
      }

      if (!socket?.connected && socket) {
        await waitForCondition(() => socket?.connected === true, 250);
      }

      sendOwnerAction({
        messageId: MESSAGE_TYPES.USER_LOGOUT,
      });

      broadcastMirrorState(
        {
          ...getMirrorSnapshot(),
          connectionState: CONNECTION_STATES.DISCONNECTED,
          clientId: null,
          isOwner: false,
          ownershipState: "passive",
          lastMessage: {
            messageId: MESSAGE_TYPES.SESSION_ENDED,
          },
        },
        {
          messageId: MESSAGE_TYPES.SESSION_ENDED,
        }
      );

      notifyListeners({
        messageId: MESSAGE_TYPES.SESSION_ENDED,
      });
    }

    disconnect();

    try {
      await httpLogout();
    } finally {
      await clearLocalSession();
    }
  };

  const disconnect = () => {
    if (socket) {
      try {
        socket.disconnect();
      } catch (error) {
        /* ignore disconnect failures */
      }
      socket = null;
    }
    pendingOwnerOnlySend = null;

    if (unsubscribeOwnership) {
      try {
        unsubscribeOwnership();
      } catch (error) {
        /* ignore */
      }
      unsubscribeOwnership = null;
    }

    if (unsubscribeChannel) {
      try {
        unsubscribeChannel();
      } catch (error) {
        /* ignore */
      }
      unsubscribeChannel = null;
    }

    ownership.stop?.();

    try {
      channel.close?.();
    } catch (error) {
      /* ignore */
    }

    mirrorStore.replaceSnapshot({
      ...getMirrorSnapshot(),
      connectionState: CONNECTION_STATES.DISCONNECTED,
      clientId: null,
      isOwner: false,
      ownershipState: "passive",
    });

    notifyListeners();
    user = null;
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    connect,
    disconnect,
    getPublicState,
    openSocket,
    performLogout,
    send,
    sendOwnerAction,
    setupEventListeners,
    subscribe,
  };
}

function createBrowserManagerForUser(user) {
  const userId = resolveUserRuntimeKey(user);
  if (!userId) {
    return null;
  }

  return createWebSocketManager({
    ownership: createTabOwnershipCoordinator({ userId }),
    mirrorStore: createRealtimeMirrorStore({
      ...DEFAULT_REALTIME_SNAPSHOT,
      ownershipState: "passive",
      isOwner: false,
      connectionState: CONNECTION_STATES.DISCONNECTED,
      clientId: null,
    }),
    channel: createTabSyncChannel({
      channelName: `${MIRROR_CHANNEL_PREFIX}:${userId}`,
    }),
  });
}

function createBrowserWebSocketStore() {
  let manager = null;
  let managerUserId = null;
  let unsubscribeManager = null;
  let publicState = { ...DEFAULT_PUBLIC_STATE };
  const listeners = new Set();

  const notify = (message = null) => {
    listeners.forEach((listener) => {
      try {
        listener({
          ...publicState,
          message,
        });
      } catch (error) {
        console.error("[Socket.IO] Error in external listener:", error);
      }
    });
  };

  const clearManagerReference = () => {
    if (unsubscribeManager) {
      try {
        unsubscribeManager();
      } catch (error) {
        /* ignore */
      }
      unsubscribeManager = null;
    }

    manager = null;
    managerUserId = null;
  };

  const attachManager = (nextManager, userId) => {
    manager = nextManager;
    managerUserId = userId;
    publicState = manager.getPublicState();
    unsubscribeManager = manager.subscribe((nextState) => {
      publicState = manager.getPublicState();
      notify(nextState.message || null);
    });
  };

  const ensureManager = (user) => {
    const userId = resolveUserRuntimeKey(user);
    if (!userId) {
      return null;
    }

    if (manager && managerUserId === userId) {
      return manager;
    }

    if (manager) {
      manager.disconnect();
      clearManagerReference();
    }

    const nextManager = createBrowserManagerForUser(user);
    if (!nextManager) {
      return null;
    }

    attachManager(nextManager, userId);
    return nextManager;
  };

  return {
    connect(user) {
      const nextManager = ensureManager(user);
      if (!nextManager) {
        return false;
      }
      const result = nextManager.connect(user);
      publicState = nextManager.getPublicState();
      return result;
    },
    disconnect() {
      if (manager) {
        manager.disconnect();
      }
      clearManagerReference();
      publicState = { ...DEFAULT_PUBLIC_STATE };
      notify();
    },
    send(message, options) {
      if (!manager) {
        return false;
      }
      return manager.send(message, options);
    },
    sendOwnerAction(message) {
      if (!manager) {
        return false;
      }
      return manager.sendOwnerAction(message);
    },
    async performLogout(options) {
      const activeManager = manager;
      if (!activeManager) {
        try {
          await options?.httpLogout?.();
        } finally {
          await options?.clearLocalSession?.();
        }
        return;
      }

      await activeManager.performLogout(options);
      clearManagerReference();
      publicState = { ...DEFAULT_PUBLIC_STATE };
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getPublicState() {
      if (manager) {
        return manager.getPublicState();
      }
      return { ...publicState };
    },
  };
}

const wsManager = createBrowserWebSocketStore();

export function useWebSocketStore() {
  const [state, setState] = useState(() => wsManager.getPublicState());

  useEffect(() => {
    const unsubscribe = wsManager.subscribe(() => {
      setState(wsManager.getPublicState());
    });

    return unsubscribe;
  }, []);

  return {
    ...state,
    connect: wsManager.connect,
    disconnect: wsManager.disconnect,
    performLogout: wsManager.performLogout,
    send: wsManager.send,
    sendOwnerAction: wsManager.sendOwnerAction,
    subscribe: wsManager.subscribe,
  };
}

export { wsManager };
